import React from "react";
import SettingsSection from "../SettingsSection";
import EmbedImageField from "./EmbedImageField";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";

export default function WelcomeSection({
  channels,
  roles,
  selectedChannel,
  setSelectedChannel,
  embedColor,
  setEmbedColor,
  welcomeTitle,
  setWelcomeTitle,
  welcomeMsg,
  setWelcomeMsg,
  welcomeFooter,
  setWelcomeFooter,
  showThumbnail,
  setShowThumbnail,
  showTimestamp,
  setShowTimestamp,
  welcomeMention,
  setWelcomeMention,
  welcomeImage,
  setWelcomeImage,
  welcomePingEnabled,
  setWelcomePingEnabled,
  welcomePingChannelId,
  setWelcomePingChannelId,
  welcomePingRoleId,
  setWelcomePingRoleId,
  welcomePingMessage,
  setWelcomePingMessage,
  guildId,
  apiBase,
  imageUploading,
  setImageUploading,
  isOpen,
  setIsOpen,
}) {
  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Sent when someone joins. Uses a Discord embed - title, description,
        optional image, and footer.
      </p>

      <label className="input-label">WELCOME CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={selectedChannel}
        onChange={setSelectedChannel}
      />

      <label className="input-label">EMBED COLOR</label>
      <input
        type="color"
        value={embedColor}
        onChange={(e) => setEmbedColor(e.target.value)}
        className="settings-input"
      />
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.75rem", marginTop: "6px" }}
      >
        Left border on this embed. Server-wide embed color is under Appearance
        in <code>/settings</code>.
      </p>

      <label className="input-label">EMBED TITLE</label>
      <input
        className="settings-input"
        value={welcomeTitle}
        onChange={(e) => setWelcomeTitle(e.target.value)}
        placeholder="☾ Nocturne"
      />

      <label className="input-label">WELCOME MESSAGE</label>
      <textarea
        value={welcomeMsg}
        onChange={(e) => setWelcomeMsg(e.target.value)}
        className="settings-input large-text"
        placeholder="**Welcome**, {user}! Check out <#channel_id>"
        style={{ minHeight: "100px" }}
      />

      <label className="input-label">FOOTER TEXT</label>
      <input
        className="settings-input"
        value={welcomeFooter}
        onChange={(e) => setWelcomeFooter(e.target.value)}
        placeholder="Nocturne is glad you're here."
      />

      <EmbedImageField
        label="EMBED IMAGE"
        helpText="Large image at the bottom of the welcome embed. Upload a file or paste a public HTTPS URL. Discord must be able to reach the URL."
        imageUrl={welcomeImage}
        onUrlChange={setWelcomeImage}
        onUploaded={setWelcomeImage}
        onCleared={() => setWelcomeImage("")}
        uploading={imageUploading}
        setUploading={setImageUploading}
        guildId={guildId}
        kind="welcome"
        apiBase={apiBase}
      />

      <div className="toggle-group">
        <label>
          <input
            type="checkbox"
            checked={welcomeMention}
            onChange={(e) => setWelcomeMention(e.target.checked)}
          />{" "}
          Mention user (pings above the embed)
        </label>
        <label>
          <input
            type="checkbox"
            checked={showThumbnail}
            onChange={(e) => setShowThumbnail(e.target.checked)}
          />{" "}
          Show User Thumbnail
        </label>
        <label>
          <input
            type="checkbox"
            checked={showTimestamp}
            onChange={(e) => setShowTimestamp(e.target.checked)}
          />{" "}
          Show Timestamp
        </label>
      </div>

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          margin: "24px 0 20px",
        }}
      />

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          marginBottom: "12px",
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(welcomePingEnabled)}
          onChange={(e) => setWelcomePingEnabled(e.target.checked)}
          style={{ accentColor: "#b85c6e" }}
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          WELCOME PING
        </span>
      </label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Separate from the welcome embed. When someone joins, ping a role in a
        channel (e.g. staff or greeters).
      </p>

      {welcomePingEnabled ? (
        <div style={{ display: "grid", gap: "12px" }}>
          <div>
            <label className="input-label">PING CHANNEL</label>
            <ChannelPicker
              channels={channels}
              value={welcomePingChannelId || ""}
              onChange={setWelcomePingChannelId}
              emptyLabel="Select ping channel..."
            />
          </div>
          <div>
            <label className="input-label">PING ROLE</label>
            <RolePicker
              roles={roles}
              value={welcomePingRoleId || ""}
              onChange={setWelcomePingRoleId}
              emptyLabel="Select role to ping..."
              placeholder="Select ping role…"
            />
          </div>
          <div>
            <label className="input-label">PING MESSAGE (OPTIONAL)</label>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "8px",
              }}
            >
              Placeholders: {"{role}"}, {"{user}"}, {"{userName}"}, {"{server}"},{" "}
              {"{memberCount}"}
            </p>
            <textarea
              className="settings-input"
              rows={3}
              value={welcomePingMessage || ""}
              placeholder="{role} — {user} just joined **{server}**."
              onChange={(e) => setWelcomePingMessage(e.target.value)}
            />
          </div>
        </div>
      ) : null}
    </SettingsSection>
  );
}
